import React, { useState, useEffect } from 'react';
import { tenantPatientAPI } from '../../../services/api/tenantPatientAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  Heart, 
  Activity, 
  TrendingUp, 
  Calendar,
  Search,
  Filter,
  Eye,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Download,
  RefreshCw
} from 'lucide-react';

const MyVitalSigns = () => {
  const [vitals, setVitals] = useState([]);
  const [filteredVitals, setFilteredVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [selectedVital, setSelectedVital] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadVitals();
  }, []);

  useEffect(() => {
    filterVitals();
  }, [searchTerm, filterPeriod, vitals]);

  // Add Esc key handler for modal
  useEscKeyHandler(showDetailModal, () => setShowDetailModal(false));

  const loadVitals = async () => {
    try {
      setLoading(true);
      const response = await tenantPatientAPI.getMyVitals();
      setVitals(response.data);
    } catch (error) {
      console.error('Failed to load vital signs:', error);
      toast.error('Failed to load vital signs');
    } finally {
      setLoading(false);
    }
  };

  const filterVitals = () => {
    let filtered = vitals;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(vital =>
        vital.rhythm?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vital.quality_flag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vital.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by time period
    if (filterPeriod !== 'all') {
      const now = new Date();
      const periodDays = {
        '7d': 7,
        '30d': 30,
        '90d': 90
      };
      
      const cutoffDate = new Date(now.getTime() - (periodDays[filterPeriod] * 24 * 60 * 60 * 1000));
      filtered = filtered.filter(vital => new Date(vital.created_at) >= cutoffDate);
    }

    setFilteredVitals(filtered);
  };

  const handleViewDetail = async (vital) => {
    try {
      const response = await tenantPatientAPI.getVitalDetail(vital.id);
      setSelectedVital(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load vital details:', error);
      toast.error('Failed to load vital details');
    }
  };

  const getHealthTrend = () => {
    if (vitals.length < 2) return null;
    
    const recent = vitals.slice(0, 5);
    const older = vitals.slice(5, 10);
    
    if (older.length === 0) return null;
    
    const recentAvg = recent.reduce((sum, v) => sum + (v.heart_rate || 0), 0) / recent.length;
    const olderAvg = older.reduce((sum, v) => sum + (v.heart_rate || 0), 0) / older.length;
    
    const change = ((recentAvg - olderAvg) / olderAvg) * 100;
    return change;
  };

  const VitalDetailModal = () => {
    if (!selectedVital) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Vital Signs Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recording Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Recording Information</h3>
                
                <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-900">
                      Recorded on {new Date(selectedVital.created_at).toLocaleDateString()}
                    </span>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                      selectedVital.quality_flag === 'High' ? 'bg-green-100 text-green-800' :
                      selectedVital.quality_flag === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedVital.quality_flag || 'Normal Quality'}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <p>Session ID: {selectedVital.session_id}</p>
                    <p>Time: {new Date(selectedVital.created_at).toLocaleTimeString()}</p>
                    {selectedVital.source_device && (
                      <p>Device: {selectedVital.source_device}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Vital Signs Data */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-va-neutral-900">Measurements</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-va-error-50 rounded-lg border border-va-error-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <Heart className="h-5 w-5 text-va-error-600" />
                      <span className="text-sm font-medium text-va-neutral-900">Heart Rate</span>
                    </div>
                    <p className="text-2xl font-bold text-va-error-600">
                      {selectedVital.heart_rate || 'N/A'}
                      {selectedVital.heart_rate && <span className="text-sm ml-1">bpm</span>}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-va-primary-50 rounded-lg border border-va-primary-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <Activity className="h-5 w-5 text-va-primary-600" />
                      <span className="text-sm font-medium text-va-neutral-900">Rhythm</span>
                    </div>
                    <p className={`text-2xl font-bold ${
                      selectedVital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(selectedVital.rhythm) 
                        ? 'text-va-error-600' 
                        : 'text-va-primary-600'
                    }`}>
                      {selectedVital.rhythm || 'Normal'}
                    </p>
                  </div>

                  {selectedVital.heart_rate_variability && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        <span className="text-sm font-medium text-gray-900">HRV</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedVital.heart_rate_variability.toFixed(1)}
                        <span className="text-sm ml-1">ms</span>
                      </p>
                    </div>
                  )}

                  {(selectedVital.blood_pressure_systolic || selectedVital.blood_pressure_diastolic) && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <TrendingUp className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-gray-900">Blood Pressure</span>
                      </div>
                      <p className="text-2xl font-bold text-green-600">
                        {selectedVital.blood_pressure_systolic}/{selectedVital.blood_pressure_diastolic}
                        <span className="text-sm ml-1">mmHg</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Clinical Notes */}
            {selectedVital.notes && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">{selectedVital.notes}</p>
                </div>
              </div>
            )}

            {/* Health Alert */}
            {selectedVital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(selectedVital.rhythm) && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-bold text-red-800">Health Alert</span>
                </div>
                <p className="text-sm text-red-700 mt-2">
                  This recording shows an irregular heart rhythm. Your healthcare team has been automatically 
                  notified and may contact you for follow-up. If you're experiencing chest pain, shortness 
                  of breath, or other concerning symptoms, please contact your doctor immediately.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-lg hover:bg-teal-700">
                Download Report
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const healthTrend = getHealthTrend();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Vital Signs</h1>
          <p className="text-gray-600">Track your heart health over time</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadVitals}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
            <Download className="h-4 w-4" />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-teal-100 mr-4">
              <Activity className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Recordings</p>
              <p className="text-2xl font-bold text-gray-900">{vitals.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-100 mr-4">
              <Heart className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Latest Heart Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {vitals.length > 0 ? vitals[0].heart_rate || 'N/A' : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100 mr-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Normal Rhythms</p>
              <p className="text-2xl font-bold text-gray-900">
                {vitals.filter(v => !v.rhythm || v.rhythm === 'Normal').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100 mr-4">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Health Trend</p>
              <p className={`text-2xl font-bold ${
                healthTrend === null ? 'text-gray-400' :
                healthTrend > 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {healthTrend === null ? 'N/A' : 
                 healthTrend > 0 ? `+${healthTrend.toFixed(1)}%` : 
                 `${healthTrend.toFixed(1)}%`
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by rhythm, quality, or notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="all">All Time</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Vitals List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Vital Signs History ({filteredVitals.length} records)
          </h3>
        </div>
        
        {filteredVitals.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vital signs found</h3>
            <p className="text-gray-500">
              {vitals.length === 0 ? 
                'Complete your first recording to start tracking your health' : 
                'Try adjusting your search or filter criteria'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredVitals.map((vital) => (
              <div key={vital.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(vital.created_at).toLocaleDateString()}
                        </span>
                                              <span className="text-sm text-va-neutral-500">
                        at {new Date(vital.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    
                    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                      vital.quality_flag === 'High' ? 'bg-va-health-100 text-va-health-800' :
                      vital.quality_flag === 'Medium' ? 'bg-va-warning-100 text-va-warning-800' :
                      'bg-va-error-100 text-va-error-800'
                    }`}>
                      {vital.quality_flag || 'Normal'}
                    </span>
                    </div>

                    <div className="mt-2 grid grid-cols-3 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-xs text-va-neutral-600">Heart Rate</p>
                        <p className="text-sm font-bold text-va-error-600">
                          {vital.heart_rate ? `${vital.heart_rate} bpm` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-va-neutral-600">Rhythm</p>
                        <p className={`text-sm font-bold ${
                          vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(vital.rhythm) 
                            ? 'text-va-error-600' 
                            : 'text-va-health-600'
                        }`}>
                          {vital.rhythm || 'Normal'}
                        </p>
                      </div>
                      {vital.heart_rate_variability && (
                        <div>
                          <p className="text-xs text-va-neutral-600">HRV</p>
                          <p className="text-sm font-bold text-brand-blue">
                            {vital.heart_rate_variability.toFixed(1)} ms
                          </p>
                        </div>
                      )}
                      {(vital.blood_pressure_systolic || vital.blood_pressure_diastolic) && (
                        <div>
                          <p className="text-xs text-va-neutral-600">Blood Pressure</p>
                          <p className="text-sm font-bold text-va-primary-600">
                            {vital.blood_pressure_systolic}/{vital.blood_pressure_diastolic}
                          </p>
                        </div>
                      )}
                    </div>

                    {vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(vital.rhythm) && (
                      <div className="mt-2 flex items-center space-x-2 p-2 bg-red-50 border border-red-200 rounded">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <span className="text-sm text-red-800">
                          Irregular rhythm detected - Healthcare team notified
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewDetail(vital)}
                      className="p-2 text-gray-400 hover:text-teal-600"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && <VitalDetailModal />}
    </div>
  );
};

export default MyVitalSigns;